// client/src/components/CatalogImportModal.jsx
// Excel import for ONE catalog section (פעולות לזיכוי / מוצרים למכירה).
//
// Flow: pick file → server parses it (POST /catalog/:list/parse, preview only,
// nothing written) → review the parsed items → "החלף" or "הוסף" drops them
// into the CatalogTab editor. The manager then saves with the regular
// "שמור שינויים" button, so the catalog keeps a single write path and the
// usual dirty-state guard applies.
//
// ALL-OR-NOTHING: a single bad row blocks the file (same rule as the points
// importer).

import { useState } from 'react';
import Modal from './Modal.jsx';
import { uploadXlsx } from '../lib/api.js';
import { formatNumber } from '../lib/currency.js';
import { ExclamationTriangleIcon, ArrowDownTrayIcon } from './Icons.jsx';

export const CATALOG_MAX_ITEMS = 50;

const BLOCK_MESSAGE = {
  empty: 'הקובץ ריק או לא תקין.',
  no_rows: 'לא נמצאו שורות נתונים בקובץ (מתחת לשורת הכותרות).',
  missing_required: 'חסרות עמודות חובה — צריך עמודת "פריט" ועמודת "סכום".',
  duplicate_columns:
    'נמצאו עמודות עם אותה כותרת. לכל עמודה חייבת להיות כותרת ייחודית — תקנו את הקובץ ונסו שוב.',
  too_many: `יותר מדי שורות — רשימה יכולה להכיל עד ${CATALOG_MAX_ITEMS} פריטים.`,
};

/**
 * @param {{ open, onClose, campId, list: 'earn'|'spend', title, symbol,
 *           isStar, currentCount: number,
 *           onApply: (items, mode: 'replace'|'append') => void }} props
 */
export default function CatalogImportModal({
  open, onClose, campId, list, title, symbol, isStar, currentCount, onApply,
}) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);

  function reset() {
    setFile(null);
    setBusy(false);
    setError(null);
    setReport(null);
  }
  function close() {
    reset();
    onClose();
  }

  async function analyze() {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      setReport(await uploadXlsx(`/api/camps/${campId}/catalog/${list}/parse`, file));
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setBusy(false);
    }
  }

  function apply(mode) {
    onApply(report.items, mode);
    close();
  }

  return (
    <Modal open={open} onClose={close} title={`ייבוא מאקסל — ${title}`} maxWidth="max-w-2xl">
      {!report ? (
        <div className="space-y-5">
          <div className="card-soft p-4">
            <h3 className="text-sm font-bold text-brand-900 mb-2">מבנה הקובץ</h3>
            <ul className="space-y-1.5 text-sm text-ink-700">
              <SpecLine label="פריט" required note="שם הפריט כפי שיופיע במסופון (עד 50 תווים)" />
              <SpecLine
                label="סכום"
                required
                note={isStar ? 'מספר שלם חיובי' : 'מספר חיובי (עד 2 ספרות אחרי הנקודה)'}
              />
              <SpecLine label="מזהה" note="מופיע בקובץ שיוצא מהמערכת — שומר על זהות הפריט. בקובץ חדש אפשר להשמיט" />
            </ul>
            <p className="mt-3 text-xs text-ink-500">
              שורה ראשונה = כותרות. העמודות מזוהות לפי שם הכותרת — הסדר לא משנה.
              הדרך הקלה: לחצו "ייצוא" כדי לקבל קובץ מוכן בפורמט הנכון, ערכו אותו ב-Excel וטענו בחזרה.
              עד {CATALOG_MAX_ITEMS} פריטים ברשימה.
            </p>
          </div>

          <DropZone file={file} setFile={setFile} />

          {error && <ErrorBanner>{error}</ErrorBanner>}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-secondary" onClick={close}>ביטול</button>
            <button type="button" className="btn-primary" onClick={analyze} disabled={!file || busy}>
              {busy ? 'קורא את הקובץ...' : 'בדיקת הקובץ'}
            </button>
          </div>
        </div>
      ) : (
        <ReviewStep
          report={report}
          symbol={symbol}
          currentCount={currentCount}
          onBack={() => { setReport(null); setError(null); }}
          onApply={apply}
        />
      )}
    </Modal>
  );
}

function SpecLine({ label, required, note }) {
  return (
    <li className="flex items-center gap-2 flex-wrap">
      <span className="w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0" />
      <span className="font-semibold">{label}</span>
      {required ? (
        <span className="badge-joy text-[10px]">חובה</span>
      ) : (
        <span className="text-xs text-ink-400">(לא חובה)</span>
      )}
      {note && <span className="text-xs text-ink-500">· {note}</span>}
    </li>
  );
}

/* ───────── Review ───────── */

function ReviewStep({ report, symbol, currentCount, onBack, onApply }) {
  const { items, problems, blocked, blockReason, columns } = report;
  const appendTotal = currentCount + items.length;
  const appendTooMany = appendTotal > CATALOG_MAX_ITEMS;

  return (
    <div className="space-y-4">
      {columns?.ignored?.length > 0 && (
        <div className="rounded-2xl bg-ink-50 border border-ink-200 text-ink-600 px-4 py-2.5 text-xs">
          עמודות בקובץ שלא יטופלו: {columns.ignored.map((c) => c.header).join(' · ')}
        </div>
      )}

      {blocked && blockReason !== 'has_problems' && (
        <ErrorBanner>{BLOCK_MESSAGE[blockReason] || 'לא ניתן לייבא את הקובץ.'}</ErrorBanner>
      )}

      {problems.length > 0 && (
        <>
          <ErrorBanner>
            יש שורות לא תקינות — הייבוא נעצר. תקנו את הקובץ ונסו שוב (הייבוא הוא הכל-או-כלום).
          </ErrorBanner>
          <div className="rounded-2xl border border-joy-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-joy-50 text-joy-900 text-xs">
                <tr>
                  <th className="text-start px-3 py-2 font-semibold w-16">שורה</th>
                  <th className="text-start px-3 py-2 font-semibold">פריט</th>
                  <th className="text-start px-3 py-2 font-semibold">בעיה</th>
                </tr>
              </thead>
              <tbody>
                {problems.slice(0, 30).map((p) => (
                  <tr key={p.row} className="border-t border-joy-100">
                    <td className="px-3 py-1.5 num text-ink-500">{p.row}</td>
                    <td className="px-3 py-1.5 text-ink-800">{p.label || <span className="text-ink-400">—</span>}</td>
                    <td className="px-3 py-1.5 text-joy-800">{p.errors.join(' · ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {problems.length > 30 && (
              <div className="px-3 py-2 text-xs text-ink-500 bg-joy-50/50">
                ועוד {problems.length - 30} שורות…
              </div>
            )}
          </div>
        </>
      )}

      {!blocked && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-ink-800">
              נמצאו <span className="num">{items.length}</span> פריטים תקינים
            </p>
            <span className="badge-slate num">ברשימה כעת: {currentCount}</span>
          </div>
          <div className="rounded-2xl border border-ink-200 max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-ink-600 text-xs sticky top-0">
                <tr>
                  <th className="text-start px-3 py-2 font-semibold w-10">#</th>
                  <th className="text-start px-3 py-2 font-semibold">פריט</th>
                  <th className="text-end px-3 py-2 font-semibold w-28">סכום</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.id || i} className="border-t border-ink-100">
                    <td className="px-3 py-1.5 num text-ink-400">{i + 1}</td>
                    <td className="px-3 py-1.5 text-ink-900 font-medium">{it.label}</td>
                    <td className="px-3 py-1.5 num text-end font-bold text-ink-800">
                      {symbol}{formatNumber(it.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-ink-500">
            הפריטים ייכנסו לעורך הרשימה. כדי לסיים — לחצו "שמור שינויים" בתחתית העמוד.
          </p>
          {appendTooMany && currentCount > 0 && (
            <p className="text-xs text-clay-800">
              הוספה לרשימה הנוכחית תגיע ל-{appendTotal} פריטים (מקסימום {CATALOG_MAX_ITEMS}) — אפשר רק להחליף.
            </p>
          )}
        </>
      )}

      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onBack}>חזרה</button>
        {!blocked && currentCount > 0 && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => onApply('append')}
            disabled={appendTooMany}
          >
            הוסף לסוף הרשימה ({items.length})
          </button>
        )}
        {!blocked && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              if (currentCount > 0 && !window.confirm(`להחליף את ${currentCount} הפריטים הקיימים ב-${items.length} הפריטים מהקובץ?`)) return;
              onApply('replace');
            }}
          >
            {currentCount > 0 ? `החלף את הרשימה (${items.length})` : `הכנס לרשימה (${items.length})`}
          </button>
        )}
      </div>
    </div>
  );
}

/* ───────── shared bits ───────── */

function DropZone({ file, setFile }) {
  const [dragging, setDragging] = useState(false);
  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  }
  return (
    <label
      onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
      onDrop={onDrop}
      className={`block rounded-2xl border-2 border-dashed transition-colors cursor-pointer
        ${dragging ? 'border-brand-500 bg-brand-50'
          : file ? 'border-mint-400 bg-mint-50'
          : 'border-ink-300 hover:border-brand-400 hover:bg-brand-50/40'}`}
    >
      <input
        type="file"
        accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="sr-only"
      />
      <div className="p-6 text-center">
        <div className={`mx-auto w-12 h-12 rounded-2xl grid place-items-center mb-2 ${file ? 'bg-mint-100 text-mint-700' : 'bg-brand-100 text-brand-700'}`}>
          <ArrowDownTrayIcon className="w-6 h-6" />
        </div>
        {file ? (
          <>
            <div className="text-sm font-semibold text-ink-900">{file.name}</div>
            <div className="text-xs text-ink-500 num mt-0.5">
              {Math.round(file.size / 1024)} KB · לחץ להחלפת קובץ
            </div>
          </>
        ) : (
          <>
            <div className="text-sm font-semibold text-ink-900">בחר קובץ XLSX</div>
            <div className="text-xs text-ink-500 mt-0.5">או גרור לכאן</div>
          </>
        )}
      </div>
    </label>
  );
}

function ErrorBanner({ children }) {
  return (
    <div className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium flex items-start gap-2">
      <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}

function humanizeError(e) {
  const code = e?.code || e?.message || '';
  if (e?.status === 413 || /too_large/.test(code)) return e?.message || 'הקובץ גדול מדי.';
  if (/file_missing/.test(code)) return 'לא נבחר קובץ.';
  if (e?.status === 403) return 'אין לך הרשאה לפעולה זו.';
  if (e?.status === 401) return 'הסשן פג. רענן את הדף והיכנס מחדש.';
  if (e?.status >= 500) return `שגיאת שרת (${e.status}). נסה שוב בעוד רגע.`;
  return `שגיאה: ${e?.message || e?.code || 'לא צפויה'}`;
}
